/**
 * Product page: show a message under Add to Cart when an add fails.
 *
 * The custom PDP form has no addToCartTextError ref, so product-form-component's own error
 * handling had nowhere to write and a failed add (quantity limit, network, etc.) did nothing
 * visible. Two sources of failure:
 * - product-form-component dispatches CartErrorEvent (bubbles from the component);
 * - fcd-spice-addons.js, which handles the add itself when addons are selected, dispatches
 *   `apollo:add-to-cart-failed` on the form.
 *
 * Asking for more than is in stock also fails: Shopify adds what's available and still
 * answers 422. So on a failure the cart is compared with how it looked when the add started:
 * if a requested line went in partly (more than 0, less than asked), the stock message shows
 * instead of the processing error (per the client). Both hide again as soon as the shopper
 * tries another add from the same form.
 */
import { StandardEvents } from '@shopify/events';

const ERROR_SEL = '[data-product-atc-error]';
const STOCK_SEL = '[data-product-atc-stock]';

/** @type {Map<string, number>} variant id -> quantity in the cart, as last seen */
let cartQuantities = readInitialQuantities();

/**
 * @typedef {{ merchandiseId: string | number, quantity: number }} RequestedLine
 * @type {WeakMap<HTMLFormElement, { lines: RequestedLine[], before: Map<string, number> }>}
 */
const addsInProgress = new WeakMap();

function readInitialQuantities() {
  try {
    const json = document.querySelector('[data-product-cart-quantities]')?.textContent || '[]';
    /** @type {Array<[number, number]>} */
    const pairs = JSON.parse(json);
    return toQuantities(pairs.map(([variant_id, quantity]) => ({ variant_id, quantity })));
  } catch {
    return new Map();
  }
}

/** @param {Array<{variant_id: number | string, quantity: number}>} items */
function toQuantities(items) {
  const quantities = new Map();
  for (const { variant_id, quantity } of items) {
    const id = String(variant_id);
    quantities.set(id, (quantities.get(id) || 0) + quantity);
  }
  return quantities;
}

/** @param {EventTarget | null} target */
function pdpFormFor(target) {
  if (!(target instanceof Element)) return null;
  const form =
    target.closest('form') ||
    target.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]');
  return form instanceof HTMLFormElement && form.closest('.custom-product-right') ? form : null;
}

/**
 * @param {HTMLFormElement | null} form
 * @param {'error' | 'stock' | null} which
 */
function showMessage(form, which) {
  const error = form?.querySelector(ERROR_SEL);
  const stock = form?.querySelector(STOCK_SEL);
  if (error instanceof HTMLElement) error.hidden = which !== 'error';
  if (stock instanceof HTMLElement) stock.hidden = which !== 'stock';
}

/** @param {string | number} merchandiseId */
const variantIdOf = (merchandiseId) => String(merchandiseId).split('/').pop() || '';

/**
 * @param {HTMLFormElement | null} form
 * @param {RequestedLine[] | undefined} lines
 */
function startAdd(form, lines) {
  if (!form) return;
  addsInProgress.set(form, { lines: Array.isArray(lines) ? lines : [], before: new Map(cartQuantities) });
  showMessage(form, null);
}

/** @param {HTMLFormElement | null} form */
async function failAdd(form) {
  if (!form) return;
  const add = addsInProgress.get(form);
  addsInProgress.delete(form);

  let partlyAdded = false;
  try {
    const cart = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((response) => response.json());
    cartQuantities = toQuantities(cart.items);
    partlyAdded = (add?.lines || []).some(({ merchandiseId, quantity }) => {
      const id = variantIdOf(merchandiseId);
      const added = (cartQuantities.get(id) || 0) - (add?.before.get(id) || 0);
      return added > 0 && added < quantity;
    });
  } catch {
    // Can't tell what made it into the cart; fall back to the processing error.
  }

  showMessage(form, partlyAdded ? 'stock' : 'error');
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  // The theme's add from this PDP form is starting (the event fires before the request).
  if (event.action === 'add') startAdd(pdpFormFor(event.target), event.lines);

  // Keep the snapshot current with every cart change on the page (drawer edits, other forms).
  event.promise
    ?.then((result) => {
      const items = result?.detail?.items;
      if (Array.isArray(items)) cartQuantities = toQuantities(items);
    })
    .catch(() => {});
});
document.addEventListener('apollo:add-to-cart-start', (event) =>
  startAdd(pdpFormFor(event.target), /** @type {CustomEvent} */ (event).detail?.lines)
);

document.addEventListener(StandardEvents.cartError, (event) => failAdd(pdpFormFor(event.target)));
document.addEventListener('apollo:add-to-cart-failed', (event) => failAdd(pdpFormFor(event.target)));
